import { useMemo } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { useMeasureText } from 'framework/dropdown/hooks/useMeasureText';
import { useFieldTheme } from 'framework/field/hooks/useFieldTheme';
import { Icon } from 'framework/Icon';
import { Seperator } from 'framework/Input/components/Seperator';
import { Width } from 'framework/theme/types';

import { Loading } from '../components/Loading';
import { useDropdownContext } from '../context/DropdownContext';

import cn from '../Dropdown.module.scss';

export const InputDropdown = () => {
  const {
    inputRef,
    placeholder,
    typed_value,
    left_icon,
    autosize,
    prefix,
    suffix,
    filterable,
    selected_index,
    synthetic_icon,
    clear,
    options,
    disabled,
    onChange,
    onFocus,
    onBlur,
    loading,
    onClick,
    onKeyDown,
    onRemove,
    theme,
  } = useDropdownContext();

  const field_theme = useFieldTheme(theme?.field);
  const measureText = useMeasureText(field_theme.general);

  const select_value = useMemo(() => {
    if (filterable) {
      return typed_value;
    }
    return `${prefix}${typed_value}${suffix}`;
  }, [typed_value, filterable, prefix, suffix]);

  const select_width = useMemo((): Width | undefined => {
    if (autosize) {
      const textWidth = measureText(select_value || placeholder);
      return `${textWidth}px`;
    }
    return undefined;
  }, [select_value, placeholder, autosize, measureText]);

  return (
    <>
      <input
        className={classNames(cn.input, {
          [cn.left]: left_icon,
          [cn.right]: synthetic_icon,
          [cn.filterable]: filterable,
        })}
        ref={inputRef}
        value={select_value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={disabled}
        readOnly={!filterable}
        style={{ width: select_width, padding: field_theme.general.padding }}
      />
      {loading && !disabled && <Loading />}
      {options[selected_index] && clear && !loading && !disabled && (
        <>
          <Icon
            icon={faXmark}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => onRemove(options[selected_index])}
            width={field_theme.general.icon_width}
            disabled={disabled}
            button
          />
          {synthetic_icon && <Seperator background={field_theme.color.disabled} />}
        </>
      )}
    </>
  );
};
